import { useCallback, useRef, useState } from 'react'
import { useCaptchaConfig } from './useCaptchaConfig'
import type { CaptchaWidgetRef } from './CaptchaWidget'
import type { CaptchaWidgetId } from './constants'

export interface UseCaptchaArgs {
  widgetId: CaptchaWidgetId
}

export interface CaptchaPayload {
  captchaToken?: string
  captchaSiteKey?: string
}

export function useCaptcha({ widgetId }: UseCaptchaArgs) {
  const { isCaptchaEnabled: captchaEnabledRaw } = useCaptchaConfig()
  const isCaptchaEnabled = Boolean(captchaEnabledRaw)
  const [captchaToken, setCaptchaToken] = useState<string | undefined>()
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>('')
  const [showCaptchaHint, setShowCaptchaHint] = useState(false)
  const captchaWidgetRef = useRef<CaptchaWidgetRef>(null)

  const resetCaptcha = useCallback(() => {
    if (!isCaptchaEnabled) return
    setCaptchaToken(undefined)
    setCaptchaSiteKey('')
    captchaWidgetRef.current?.reset()
  }, [isCaptchaEnabled])

  const handleCaptchaVerify = useCallback((token: string, siteKey: string) => {
    setCaptchaToken(token)
    setCaptchaSiteKey(siteKey)
    setShowCaptchaHint(false)
  }, [])

  const isCaptchaReady = useCallback(() => {
    if (!isCaptchaEnabled) return true
    return Boolean(captchaToken)
  }, [isCaptchaEnabled, captchaToken])

  const getCaptchaPayload = useCallback((): CaptchaPayload => {
    if (isCaptchaEnabled && captchaToken) {
      return {
        captchaToken,
        captchaSiteKey,
      }
    }
    return {}
  }, [isCaptchaEnabled, captchaToken, captchaSiteKey])

  const getAuthEndpoint = useCallback(
    ({ regular, secure }: { regular: string; secure: string }) => {
      return isCaptchaEnabled ? secure : regular
    },
    [isCaptchaEnabled]
  )

  return {
    widgetId,
    isCaptchaEnabled,
    captchaWidgetRef,
    showCaptchaHint,
    setShowCaptchaHint,
    handleCaptchaVerify,
    resetCaptcha,
    isCaptchaReady,
    getCaptchaPayload,
    getAuthEndpoint,
  } as const
}

export type UseCaptchaReturn = ReturnType<typeof useCaptcha>
